import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { Home, Calendar, Heart, MoreHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";

/**
 * Bottom tab bar for the client portal on mobile.
 *
 * The 3 primary tabs (Home / Bookings / Planning) are hardcoded for
 * prominence. Everything else — 17+ items — lives inside the "More"
 * drawer, which is driven by the same /api/mobile-navigation/client
 * endpoint as the hamburger sidebar so there is a SINGLE source of
 * truth for the full mobile menu and it never drifts from desktop.
 *
 * Previous versions had hardcoded moreItems with wrong paths
 * (/addons instead of /client/addons, /wedding-website instead of
 * /website-v2, /budget-calculator instead of /budget, etc.) and the
 * Transport feature was missing entirely. All of that is gone — the
 * drawer now renders whatever the API returns.
 */

// Hardcoded primary tabs — the 3 most-used pages for a client on mobile.
// Paths match the desktop sidebar.
const PRIMARY_TABS = [
  { icon: Home, label: "Home", path: "/" },
  { icon: Calendar, label: "Bookings", path: "/bookings" },
  { icon: Heart, label: "Planning", path: "/website-v2" },
];

// Fallback items used ONLY if /api/mobile-navigation/client returns empty.
// Mirrors the desktop sidebar (client/src/components/layout/sidebar.tsx
// getClientNavigation function). The drawer skips items whose path is
// already represented by a primary tab, so no duplicates.
const FALLBACK_MORE_ITEMS: Array<{ label: string; path: string; icon: string }> = [
  // Main section
  { label: "Dashboard", path: "/", icon: "Home" },
  { label: "Help & Support", path: "/support", icon: "HelpCircle" },
  { label: "Bookings", path: "/bookings", icon: "Calendar" },
  { label: "Invoices", path: "/invoices", icon: "CreditCard" },
  { label: "Event Form (New)", path: "/event-form", icon: "Palette" },
  { label: "Story Weaver", path: "/story-weaver", icon: "Film" },
  { label: "MC Scheduling", path: "/mc-scheduling", icon: "Mic" },
  { label: "Live Stream", path: "/live-stream", icon: "Radio" },
  { label: "Add-ons & Services", path: "/client/addons", icon: "ShoppingBag" },
  { label: "Loyalty Program", path: "/loyalty-program", icon: "Gift" },
  { label: "Family Hub", path: "/family-hub", icon: "Users" },
  { label: "Transport", path: "/client-transport", icon: "Car" },
  // Wedding Planning section
  { label: "Wedding Website", path: "/website-v2", icon: "Globe" },
  { label: "Budget Calculator", path: "/budget", icon: "Calculator" },
  { label: "Wedding Invitations", path: "/invitations", icon: "Mail" },
  { label: "Vendor Directory", path: "/vendors", icon: "Store" },
  { label: "Wedding Checklist", path: "/checklist", icon: "CheckSquare" },
  { label: "Timeline & Tasks", path: "/ai-timeline", icon: "Calendar" },
];

// Href of the first Wedding Planning item — used to render a divider
// inside the More drawer so the two sections stay visually separated.
const WEDDING_PLANNING_START = "/website-v2";

interface ApiNavItem {
  href: string;
  label: string;
  icon: string;
}

export default function MobileTabBar() {
  const [location] = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { user } = useAuth();

  // Pull the same client nav items the hamburger menu uses, so the
  // More drawer stays in perfect sync with the drawer.
  const { data: apiItems = [] } = useQuery<ApiNavItem[]>({
    queryKey: [`/api/mobile-navigation/${user?.role || 'client'}`],
    enabled: !!user?.role,
    staleTime: 30_000,
  });

  // Normalize API vs fallback to a single shape with { label, path, icon }.
  let items = (apiItems.length > 0
    ? apiItems.map((i) => ({ label: i.label, path: i.href, icon: i.icon }))
    : FALLBACK_MORE_ITEMS);

  // Help & Support — always reachable even when the DB-managed navigation
  // hasn't been updated to include it yet (Support Center S1.2).
  if (!items.some((i) => i.path === "/support")) {
    items = [items[0], { label: "Help & Support", path: "/support", icon: "HelpCircle" }, ...items.slice(1)];
  }

  // Filter out items already represented by a primary tab.
  const primaryPaths = new Set(PRIMARY_TABS.map((t) => t.path));
  const moreItems = items.filter((i) => !primaryPaths.has(i.path));

  const getIcon = (iconName: string) => {
    const IconComponent = Icons[iconName as keyof typeof Icons] as React.ComponentType<any>;
    return IconComponent || Icons.Home;
  };

  const isActive = (path: string) => location === path;
  const isMoreActive = moreItems.some((i) => isActive(i.path));

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-t border-white/10"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex items-center justify-around h-14">
        {PRIMARY_TABS.map((tab) => (
          <Link key={tab.path} href={tab.path}>
            <button className="flex flex-col items-center gap-0.5 px-3 py-1">
              <tab.icon
                size={20}
                className={
                  isActive(tab.path) ? "text-[#B8860B]" : "text-white/50"
                }
              />
              <span
                className={`text-[10px] ${
                  isActive(tab.path)
                    ? "text-[#B8860B] font-medium"
                    : "text-white/50"
                }`}
              >
                {tab.label}
              </span>
            </button>
          </Link>
        ))}

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button className="flex flex-col items-center gap-0.5 px-3 py-1">
              <MoreHorizontal
                size={20}
                className={isMoreActive ? "text-[#B8860B]" : "text-white/50"}
              />
              <span
                className={`text-[10px] ${
                  isMoreActive ? "text-[#B8860B] font-medium" : "text-white/50"
                }`}
              >
                More
              </span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="bg-gray-950 border-white/10 rounded-t-2xl max-h-[70vh] overflow-y-auto"
          >
            <div className="space-y-1">
              {moreItems.map((item) => {
                const Icon = getIcon(item.icon);
                const isPlanningStart = item.path === WEDDING_PLANNING_START;
                return (
                  <div key={item.path}>
                    {isPlanningStart && (
                      <p className="text-xs uppercase tracking-wider text-[#B8860B]/50 px-2 pt-4 pb-2 mt-3 border-t border-white/10">
                        Wedding Planning
                      </p>
                    )}
                    <Link href={item.path}>
                      <button
                        onClick={() => setSheetOpen(false)}
                        className={`w-full text-left px-4 py-3 rounded-lg text-sm flex items-center gap-3 ${
                          isActive(item.path)
                            ? "text-[#B8860B] bg-[#B8860B]/10"
                            : "text-white/70 hover:bg-white/5"
                        }`}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    </Link>
                  </div>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
